import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faChartPie,
  faCircleExclamation,
  faCoins,
  faLandmark,
  faRotateRight,
  faScaleBalanced,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import html2pdf from 'html2pdf.js';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { app } from '../../firebase';
import logo from '../../Images/logo-main-black.png';
import '../dash.css';
import {
  buildPayrollRows,
  filterSchoolFeeIncomeRecords,
  formatKes,
  formatShortDate,
  getMonthKey,
  normalizeAmount,
  toDateValue,
  isSuccessfulBillPayment,
  isSuccessfulRequisitionPayment,
  isSuccessfulSalaryPayment
} from '../../utils/financeWorkflow';

const ACTIVE_PAYMENT_STATUSES = ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'approved'];
const ASSET_STATUS_EXCLUSIONS = ['disposed', 'written off', 'written-off', 'sold', 'retired'];
const CHART_COLORS = ['#111827', '#64748b', '#a3aed0'];

const normalizeText = (value) => String(value || '').trim();

const isSuccessfulStatus = (value) => ACTIVE_PAYMENT_STATUSES.includes(normalizeText(value).toLowerCase());

const formatAccountingKes = (value) => {
  const amount = normalizeAmount(value);
  const formatted = formatKes(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};

const formatDateTime = (value) => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return '—';
  }

  return dateValue.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const buildTotalsMap = (items, getKey, getAmount, getDate) => {
  return items.reduce((accumulator, item) => {
    const key = normalizeText(getKey(item));
    if (!key) {
      return accumulator;
    }

    if (!accumulator[key]) {
      accumulator[key] = {
        key,
        amount: 0,
        count: 0,
        latestDate: null
      };
    }

    const bucket = accumulator[key];
    bucket.amount += normalizeAmount(getAmount(item));
    bucket.count += 1;

    const itemDate = toDateValue(getDate(item));
    if (itemDate && (!bucket.latestDate || itemDate > bucket.latestDate)) {
      bucket.latestDate = itemDate;
    }

    return accumulator;
  }, {});
};

function BalanceSheetComp() {
  const db = getFirestore(app);
  const currentMonthKey = getMonthKey();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [income, setIncome] = useState([]);
  const [admissionFees, setAdmissionFees] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [assets, setAssets] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPayments, setBillPayments] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [requisitionPayments, setRequisitionPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [statutoryRemittances, setStatutoryRemittances] = useState([]);
  const [statutoryPayments, setStatutoryPayments] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loanPayments, setLoanPayments] = useState([]);
  const [legacyExpenses, setLegacyExpenses] = useState([]);
  const [marketingExpenses, setMarketingExpenses] = useState([]);
  const statementRef = useRef(null);

  const loadPage = useCallback(async () => {
    const [
      incomeSnapshot,
      admissionSnapshot,
      otherIncomeSnapshot,
      studentSnapshot,
      courseSnapshot,
      assetSnapshot,
      billSnapshot,
      billPaymentSnapshot,
      requisitionSnapshot,
      requisitionPaymentSnapshot,
      employeeSnapshot,
      salaryPaymentSnapshot,
      statutorySnapshot,
      statutoryPaymentSnapshot,
      loanSnapshot,
      loanPaymentSnapshot,
      expenseSnapshot,
      marketingExpenseSnapshot
    ] = await Promise.all([
      getDocs(collection(db, 'Income')),
      getDocs(collection(db, 'AdmissionFeePayments')),
      getDocs(collection(db, 'otherIncome')),
      getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
      getDocs(collection(db, 'courses')),
      getDocs(collection(db, 'AssetRegister')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments')),
      getDocs(collection(db, 'Requisitions')),
      getDocs(collection(db, 'RequisitionPayments')),
      getDocs(collection(db, 'Employees')),
      getDocs(collection(db, 'SalaryPayments')),
      getDocs(collection(db, 'StatutoryRemittances')),
      getDocs(collection(db, 'StatutoryRemittancePayments')),
      getDocs(collection(db, 'Loans')),
      getDocs(collection(db, 'LoanPayments')),
      getDocs(collection(db, 'Expenses')),
      getDocs(collection(db, 'marketingExpenses'))
    ]);

    setIncome(incomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setAdmissionFees(admissionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setOtherIncome(otherIncomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStudents(studentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCourses(courseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setAssets(assetSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBillPayments(billPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitions(requisitionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitionPayments(requisitionPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setSalaryPayments(salaryPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStatutoryRemittances(statutorySnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStatutoryPayments(statutoryPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLoans(loanSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLoanPayments(loanPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLegacyExpenses(expenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMarketingExpenses(marketingExpenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setSnapshotAt(new Date());
  }, [db]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        setError('');
        await loadPage();
      } catch (pageError) {
        console.error('Failed to load balance sheet:', pageError);
        setError('Balance sheet could not be loaded right now.');
        swal('Error', 'Balance sheet could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadPage]);

  const courseMap = useMemo(() => {
    return courses.reduce((accumulator, course) => {
      accumulator[course.mcourseName] = {
        price: normalizeAmount(course.mcoursePrice),
        installment: normalizeAmount(course.feeInstallments)
      };
      return accumulator;
    }, {});
  }, [courses]);

  const schoolFeeIncome = useMemo(() => filterSchoolFeeIncomeRecords(income), [income]);

  const incomeByStudent = useMemo(() => {
    return schoolFeeIncome.reduce((accumulator, record) => {
      const studentId = normalizeText(record.studentId || record.userId);
      if (!studentId) {
        return accumulator;
      }

      if (!accumulator[studentId]) {
        accumulator[studentId] = [];
      }

      accumulator[studentId].push({
        amount: normalizeAmount(record.incomeAmount || record.amount),
        paymentDate: toDateValue(record.paymentDate || record.timeCreated || record.createdAt)
      });

      return accumulator;
    }, {});
  }, [schoolFeeIncome]);

  const schoolFeeIncomeTotal = useMemo(() => {
    return schoolFeeIncome.reduce((sum, record) => sum + normalizeAmount(record.incomeAmount || record.amount), 0);
  }, [schoolFeeIncome]);

  const admissionFeeTotal = useMemo(() => {
    return admissionFees.reduce((sum, record) => sum + normalizeAmount(record.amount || record.admissionFeeAmount), 0);
  }, [admissionFees]);

  const otherIncomeTotal = useMemo(() => {
    return otherIncome.reduce((sum, record) => sum + normalizeAmount(record.amount), 0);
  }, [otherIncome]);

  const recordedInflowsTotal = schoolFeeIncomeTotal + admissionFeeTotal + otherIncomeTotal;

  const studentRows = useMemo(() => {
    return students
      .map((student) => {
        const studentId = normalizeText(student.userId || student.id);
        const courseName = normalizeText(student.course);
        const course = courseMap[courseName];

        if (!studentId || !course || course.price <= 0) {
          return null;
        }

        const payments = incomeByStudent[studentId] || [];
        const paidAmount = payments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const balance = Math.max(course.price - paidAmount, 0);

        if (balance <= 0) {
          return null;
        }

        const latestPayment = [...payments].sort((first, second) => {
          const firstTime = toDateValue(first.paymentDate)?.getTime() || 0;
          const secondTime = toDateValue(second.paymentDate)?.getTime() || 0;
          return secondTime - firstTime;
        })[0] || null;

        return {
          id: studentId,
          studentName: student.bio || 'Student',
          course: courseName || 'Programme not set',
          campus: student.branchName || student.campus || 'Campus not set',
          totalCourseFee: course.price,
          paidAmount,
          balance,
          latestPaymentDate: latestPayment?.paymentDate || null
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.balance - first.balance);
  }, [courseMap, incomeByStudent, students]);

  const accountsReceivableTotal = useMemo(() => {
    return studentRows.reduce((sum, row) => sum + normalizeAmount(row.balance), 0);
  }, [studentRows]);

  const assetRows = useMemo(() => {
    return assets
      .map((asset) => {
        const status = normalizeText(asset.status || asset.assetStatus || 'On Service').toLowerCase();

        if (ASSET_STATUS_EXCLUSIONS.some((word) => status.includes(word))) {
          return null;
        }

        const amount = normalizeAmount(asset.currentValue || asset.purchasePrice || asset.assetValue);
        if (amount <= 0) {
          return null;
        }

        return {
          id: asset.id,
          assetName: asset.assetName || 'Asset',
          assetCategory: asset.assetCategory || 'Uncategorised',
          campus: asset.selectedCampus || asset.campus || 'General',
          amount,
          status: asset.status || 'On Service',
          purchaseDate: toDateValue(asset.dateOfPurchase || asset.timeStamp || asset.createdAt)
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.amount - first.amount);
  }, [assets]);

  const assetCategoryRows = useMemo(() => {
    const grouped = buildTotalsMap(
      assetRows,
      (item) => item.assetCategory,
      (item) => item.amount,
      (item) => item.purchaseDate
    );

    return Object.values(grouped)
      .map((bucket) => ({
        category: bucket.key,
        amount: bucket.amount,
        count: bucket.count,
        latestDate: bucket.latestDate
      }))
      .sort((first, second) => second.amount - first.amount);
  }, [assetRows]);

  const nonCurrentAssetsTotal = useMemo(() => {
    return assetRows.reduce((sum, row) => sum + normalizeAmount(row.amount), 0);
  }, [assetRows]);

  const billRows = useMemo(() => {
    return bills
      .map((bill) => {
        const relatedPayments = billPayments
          .filter((payment) => normalizeText(payment.billId) === bill.id && isSuccessfulBillPayment(payment))
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const expectedAmount = normalizeAmount(bill.billAmount || bill.amount);
        const paidAmount = relatedPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

        if (remainingAmount <= 0) {
          return null;
        }

        return {
          id: bill.id,
          title: bill.billTitle || bill.billName || 'Fixed bill',
          counterparty: bill.vendorName || bill.payee || 'Vendor',
          expectedAmount,
          paidAmount,
          remainingAmount,
          lastPaymentDate: relatedPayments[0] ? toDateValue(relatedPayments[0].paymentDate || relatedPayments[0].createdAt) : null
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [billPayments, bills]);

  const requisitionRows = useMemo(() => {
    return requisitions
      .map((item) => {
        const relatedPayments = requisitionPayments
          .filter((payment) => normalizeText(payment.requisitionId) === item.id && isSuccessfulRequisitionPayment(payment))
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const expectedAmount = normalizeAmount(item.totalAmount || item.amount);
        const paidAmount = relatedPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

        if (remainingAmount <= 0) {
          return null;
        }

        return {
          id: item.id,
          title: item.itemTitle || item.requisitionTitle || 'Requisition',
          counterparty: item.requesterName || item.createdByName || 'Requester',
          expectedAmount,
          paidAmount,
          remainingAmount,
          lastPaymentDate: relatedPayments[0] ? toDateValue(relatedPayments[0].paymentDate || relatedPayments[0].createdAt) : null
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [requisitionPayments, requisitions]);

  const payrollRows = useMemo(() => {
    return buildPayrollRows({
      employees,
      payments: salaryPayments,
      periodKey: currentMonthKey
    })
      .filter((item) => normalizeText(item.employeeStatus || 'Active').toLowerCase() !== 'archived')
      .filter((item) => item.totalOutstanding > 0);
  }, [currentMonthKey, employees, salaryPayments]);

  const currentPayrollOutstandingTotal = useMemo(() => {
    return payrollRows.reduce((sum, row) => sum + normalizeAmount(row.totalOutstanding), 0);
  }, [payrollRows]);

  const statutoryRows = useMemo(() => {
    return statutoryRemittances
      .map((remittance) => {
        const remittancePayments = statutoryPayments
          .filter((payment) => normalizeText(payment.remittanceId) === remittance.id && isSuccessfulStatus(payment.paymentStatus || payment.status))
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const expectedAmount = normalizeAmount(remittance.amount || remittance.expectedAmount);
        const paidAmount = remittancePayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

        if (remainingAmount <= 0) {
          return null;
        }

        return {
          id: remittance.id,
          remittanceType: remittance.remittanceType || 'Statutory',
          recipientName: remittance.recipientName || 'Statutory body',
          periodKey: remittance.periodKey || getMonthKey(remittance.dueDate || remittance.createdAt),
          expectedAmount,
          paidAmount,
          remainingAmount,
          dueDate: toDateValue(remittance.dueDate),
          lastPaymentDate: remittancePayments[0] ? toDateValue(remittancePayments[0].paymentDate || remittancePayments[0].createdAt) : null
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [statutoryPayments, statutoryRemittances]);

  const statutoryOutstandingTotal = useMemo(() => {
    return statutoryRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0);
  }, [statutoryRows]);

  const loanRows = useMemo(() => {
    const currentThreshold = new Date();
    currentThreshold.setMonth(currentThreshold.getMonth() + 12);

    return loans
      .map((loan) => {
        const relatedPayments = loanPayments
          .filter((payment) => {
            const paymentLoanId = normalizeText(payment.loanId || payment.liabilityId || payment.debtId);
            return paymentLoanId === loan.id && isSuccessfulStatus(payment.paymentStatus || payment.status);
          })
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const principalAmount = normalizeAmount(loan.principalAmount || loan.loanAmount || loan.amount);
        const paidAmount = relatedPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(principalAmount - paidAmount, 0);
        const dueDate = toDateValue(loan.dueDate);
        const isCurrent = Boolean(dueDate) && dueDate <= currentThreshold;

        if (remainingAmount <= 0) {
          return null;
        }

        return {
          id: loan.id,
          title: loan.liabilityTitle || loan.loanDesc || loan.loanTitle || 'Loan',
          counterparty: loan.counterpartyName || loan.lenderName || 'Lender',
          principalAmount,
          paidAmount,
          remainingAmount,
          dueDate,
          isCurrent,
          lastPaymentDate: relatedPayments[0] ? toDateValue(relatedPayments[0].paymentDate || relatedPayments[0].createdAt) : null
        };
      })
      .filter(Boolean)
      .sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [loanPayments, loans]);

  const currentLoanRows = useMemo(() => {
    return loanRows.filter((row) => row.isCurrent);
  }, [loanRows]);

  const longTermLoanRows = useMemo(() => {
    return loanRows.filter((row) => !row.isCurrent);
  }, [loanRows]);

  const currentLoanOutstandingTotal = useMemo(() => {
    return currentLoanRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0);
  }, [currentLoanRows]);

  const longTermLoanOutstandingTotal = useMemo(() => {
    return longTermLoanRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0);
  }, [longTermLoanRows]);

  const currentLiabilitiesTotal = billRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0)
    + requisitionRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0)
    + currentPayrollOutstandingTotal
    + statutoryOutstandingTotal
    + currentLoanOutstandingTotal;

  const totalLiabilities = currentLiabilitiesTotal + longTermLoanOutstandingTotal;

  const financeOutflowTotal = useMemo(() => {
    const billOutflow = billPayments
      .filter((payment) => isSuccessfulBillPayment(payment))
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const requisitionOutflow = requisitionPayments
      .filter((payment) => isSuccessfulRequisitionPayment(payment))
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const payrollOutflow = salaryPayments
      .filter((payment) => isSuccessfulSalaryPayment(payment))
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const statutoryOutflow = statutoryPayments
      .filter((payment) => isSuccessfulStatus(payment.paymentStatus || payment.status))
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const loanOutflow = loanPayments
      .filter((payment) => isSuccessfulStatus(payment.paymentStatus || payment.status))
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const marketingOutflow = marketingExpenses.reduce((sum, item) => sum + normalizeAmount(item.expenseAmount || item.amount), 0);
    const legacyOutflow = legacyExpenses.reduce((sum, item) => sum + normalizeAmount(item.expenseAmount || item.amount), 0);

    return billOutflow
      + requisitionOutflow
      + payrollOutflow
      + statutoryOutflow
      + loanOutflow
      + marketingOutflow
      + legacyOutflow;
  }, [billPayments, legacyExpenses, loanPayments, marketingExpenses, requisitionPayments, salaryPayments, statutoryPayments]);

  const trackedCashAndBank = recordedInflowsTotal - financeOutflowTotal;
  const totalAssets = trackedCashAndBank + accountsReceivableTotal + nonCurrentAssetsTotal;
  const equity = totalAssets - totalLiabilities;
  const balanceStatusTone = equity >= 0 ? 'success' : 'danger';
  const totalLiabilitiesAndEquity = totalLiabilities + equity;

  const statementAssetRows = useMemo(() => ([
    {
      section: 'Current assets',
      rows: [
        {
          label: 'Cash and cash equivalents',
          note: 'Cash position derived from recorded inflows less recorded outflows.',
          amount: trackedCashAndBank
        },
        {
          label: 'Trade and other receivables',
          note: `${studentRows.length} student fee balance${studentRows.length === 1 ? '' : 's'} still open.`,
          amount: accountsReceivableTotal
        }
      ],
      totalLabel: 'Total current assets',
      totalAmount: trackedCashAndBank + accountsReceivableTotal
    },
    {
      section: 'Non-current assets',
      rows: assetCategoryRows.length > 0
        ? assetCategoryRows.slice(0, 5).map((row) => ({
          label: row.category,
          note: `${row.count} asset${row.count === 1 ? '' : 's'} • Last updated ${formatShortDate(row.latestDate)}`,
          amount: row.amount
        }))
        : [{
          label: 'Property, plant and equipment',
          note: 'Asset register carrying value.',
          amount: nonCurrentAssetsTotal
        }],
      totalLabel: 'Total non-current assets',
      totalAmount: nonCurrentAssetsTotal
    }
  ]), [
    accountsReceivableTotal,
    assetCategoryRows,
    nonCurrentAssetsTotal,
    studentRows.length,
    trackedCashAndBank
  ]);

  const statementLiabilityRows = useMemo(() => ([
    {
      section: 'Current liabilities',
      rows: [
        {
          label: 'Bills payable',
          note: 'Outstanding vendor and supplier balances.',
          amount: billRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0)
        },
        {
          label: 'Requisition payables',
          note: 'Approved requisitions not yet fully settled.',
          amount: requisitionRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0)
        },
        {
          label: 'Payroll payables',
          note: 'Unsettled salary obligations from active payroll history.',
          amount: currentPayrollOutstandingTotal
        },
        {
          label: 'Statutory payables',
          note: 'PAYE, NSSF, SHA, and related obligations.',
          amount: statutoryOutstandingTotal
        },
        {
          label: 'Current portion of borrowings',
          note: 'Loan balances expected within the next 12 months.',
          amount: currentLoanOutstandingTotal
        }
      ],
      totalLabel: 'Total current liabilities',
      totalAmount: currentLiabilitiesTotal
    },
    {
      section: 'Non-current liabilities',
      rows: [
        {
          label: 'Long-term borrowings',
          note: 'Borrowings due after the next 12 months.',
          amount: longTermLoanOutstandingTotal
        }
      ],
      totalLabel: 'Total non-current liabilities',
      totalAmount: longTermLoanOutstandingTotal
    },
    {
      section: 'Equity',
      rows: [
        {
          label: 'Accumulated surplus / (deficit)',
          note: 'Balancing figure from the live management snapshot.',
          amount: equity
        }
      ],
      totalLabel: 'Total equity',
      totalAmount: equity
    }
  ]), [
    billRows,
    currentLoanOutstandingTotal,
    currentLiabilitiesTotal,
    currentPayrollOutstandingTotal,
    equity,
    longTermLoanOutstandingTotal,
    requisitionRows,
    statutoryOutstandingTotal
  ]);

  const assetMixData = useMemo(() => {
    return [
      { name: 'Cash & bank', value: Math.max(trackedCashAndBank, 0) },
      { name: 'Receivables', value: Math.max(accountsReceivableTotal, 0) },
      { name: 'Non-current assets', value: Math.max(nonCurrentAssetsTotal, 0) }
    ].filter((item) => item.value > 0);
  }, [accountsReceivableTotal, nonCurrentAssetsTotal, trackedCashAndBank]);

  const summaryCards = useMemo(() => ([
    {
      label: 'Total assets',
      value: totalAssets,
      note: 'Cash, receivables, and the asset register combined.',
      tone: 'primary'
    },
    {
      label: 'Total liabilities',
      value: totalLiabilities,
      note: 'Bills, requisitions, payroll, loans, and statutory obligations still open.',
      tone: 'warning'
    },
    {
      label: 'Equity',
      value: equity,
      note: 'Residual value after liabilities are deducted from tracked assets.',
      tone: balanceStatusTone
    },
    {
      label: 'Tracked cash & bank',
      value: trackedCashAndBank,
      note: 'Recorded inflows minus recorded outflows across the app.',
      tone: trackedCashAndBank >= 0 ? 'success' : 'danger'
    }
  ]), [balanceStatusTone, equity, totalAssets, totalLiabilities, trackedCashAndBank]);

  const currentAssetRows = useMemo(() => ([
    {
      label: 'Cash and bank',
      amount: trackedCashAndBank,
      note: 'Recorded inflows minus confirmed outflows from finance pages.'
    },
    {
      label: 'Accounts receivable',
      amount: accountsReceivableTotal,
      note: `${studentRows.length} students still carrying an unpaid fee balance.`
    }
  ]), [accountsReceivableTotal, studentRows.length, trackedCashAndBank]);

  const liabilityBreakdownRows = useMemo(() => ([
    {
      label: 'Bills payable',
      amount: billRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0),
      note: 'Outstanding fixed bills and supplier commitments.'
    },
    {
      label: 'Requisitions payable',
      amount: requisitionRows.reduce((sum, row) => sum + normalizeAmount(row.remainingAmount), 0),
      note: 'Approved requisitions still awaiting full settlement.'
    },
    {
      label: 'Payroll payable',
      amount: currentPayrollOutstandingTotal,
      note: 'Unsettled salary obligations from the active payroll history.'
    },
    {
      label: 'Statutory remittances',
      amount: statutoryOutstandingTotal,
      note: 'PAYE, NSSF, SHA, housing levy, and other legal obligations.'
    },
    {
      label: 'Current loan portion',
      amount: currentLoanOutstandingTotal,
      note: 'Loan balances due within the next 12 months.'
    },
    {
      label: 'Long-term loans',
      amount: longTermLoanOutstandingTotal,
      note: 'Loan balances treated as non-current liabilities.'
    }
  ]), [
    billRows,
    currentLoanOutstandingTotal,
    currentPayrollOutstandingTotal,
    longTermLoanOutstandingTotal,
    requisitionRows,
    statutoryOutstandingTotal
  ]);

  const sourceRows = useMemo(() => ([
    {
      label: 'Income records',
      count: schoolFeeIncome.length,
      amount: schoolFeeIncomeTotal,
      note: 'School-fee income after admission-fee records are filtered out.'
    },
    {
      label: 'Admission fee payments',
      count: admissionFees.length,
      amount: admissionFeeTotal,
      note: 'Applicant and prospect admissions recorded in the app.'
    },
    {
      label: 'Other income',
      count: otherIncome.length,
      amount: otherIncomeTotal,
      note: 'Non-fee income from the other income module.'
    },
    {
      label: 'Asset register',
      count: assetRows.length,
      amount: nonCurrentAssetsTotal,
      note: 'Book value from the assets module.'
    },
    {
      label: 'Open liabilities',
      count: billRows.length + requisitionRows.length + payrollRows.length + statutoryRows.length + loanRows.length,
      amount: totalLiabilities,
      note: 'Bills, requisitions, payroll, statutory, and loan commitments.'
    }
  ]), [
    admissionFeeTotal,
    admissionFees.length,
    assetRows.length,
    billRows.length,
    loanRows.length,
    nonCurrentAssetsTotal,
    otherIncome.length,
    otherIncomeTotal,
    payrollRows.length,
    requisitionRows.length,
    schoolFeeIncomeTotal,
    schoolFeeIncome.length,
    statutoryRows.length,
    totalLiabilities
  ]);

  const topReceivables = useMemo(() => studentRows.slice(0, 6), [studentRows]);
  const topAssets = useMemo(() => assetRows.slice(0, 6), [assetRows]);
  const topLiabilities = useMemo(() => {
    const combined = [
      ...billRows.map((item) => ({
        id: `bill-${item.id}`,
        title: item.title,
        meta: item.counterparty,
        amount: item.remainingAmount,
        date: item.lastPaymentDate,
        tone: 'danger'
      })),
      ...requisitionRows.map((item) => ({
        id: `requisition-${item.id}`,
        title: item.title,
        meta: item.counterparty,
        amount: item.remainingAmount,
        date: item.lastPaymentDate,
        tone: 'warning'
      })),
      ...payrollRows.map((item) => ({
        id: `payroll-${item.id}`,
        title: item.employeeName || 'Employee',
        meta: item.jobPosition || item.campus || 'Salary',
        amount: item.totalOutstanding,
        date: item.lastPaymentDate,
        tone: 'info'
      })),
      ...statutoryRows.map((item) => ({
        id: `statutory-${item.id}`,
        title: item.remittanceType,
        meta: item.recipientName || 'Statutory body',
        amount: item.remainingAmount,
        date: item.lastPaymentDate || item.dueDate,
        tone: 'warning'
      })),
      ...loanRows.map((item) => ({
        id: `loan-${item.id}`,
        title: item.title,
        meta: item.counterparty,
        amount: item.remainingAmount,
        date: item.lastPaymentDate || item.dueDate,
        tone: item.isCurrent ? 'warning' : 'neutral'
      }))
    ];

    return combined
      .sort((first, second) => second.amount - first.amount)
      .slice(0, 8);
  }, [billRows, loanRows, payrollRows, requisitionRows, statutoryRows]);

  const pieTooltip = useCallback(({ active, payload }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const item = payload[0].payload;

    return (
      <div className="balanceSheetTooltip">
        <strong>{item.name}</strong>
      <span>{formatKes(item.value)}</span>
    </div>
  );
  }, []);

  const exportBalanceSheetPdf = useCallback(() => {
    if (!statementRef.current) {
      return;
    }

    const fileLabel = snapshotAt
      ? snapshotAt.toLocaleDateString('en-KE', { month: 'short', year: 'numeric' }).replace(/\s+/g, '-')
      : 'current';

    html2pdf()
      .from(statementRef.current)
      .set({
        margin: 10,
        filename: `ubunifu-college-balance-sheet-${fileLabel}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      })
      .save();
  }, [snapshotAt]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError('');
      await loadPage();
    } catch (pageError) {
      console.error('Failed to refresh balance sheet:', pageError);
      setError('Balance sheet could not be refreshed right now.');
      swal('Error', 'Balance sheet could not be refreshed right now.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientsComp applicationsPage financeReportsPage balanceSheetPage">
      <section className="appsHeader financeReportsHeader balanceSheetHero">
        <div className="financeReportsHeroCopy balanceSheetHeroCopy">
          <p className="appsKicker">Finance reports</p>
          <h4>Balance sheet</h4>
          <p className="appsSubtext balanceSheetHeroText">
            A live operational balance sheet built from income, admissions, assets, bills,
            requisitions, payroll, statutory obligations, and loan records across the app.
          </p>
          <div className="balanceSheetHeroMeta">
            <span>
              <FontAwesomeIcon icon={faCoins} /> {studentRows.length} fee balances open
            </span>
            <span>
              <FontAwesomeIcon icon={faLandmark} /> {assetRows.length} tracked assets
            </span>
            <span>
              <FontAwesomeIcon icon={faScaleBalanced} /> {topLiabilities.length} top liabilities shown
            </span>
          </div>
        </div>
        <div className="appsHeaderStats financeReportsHeaderStats balanceSheetHeaderStats">
          {summaryCards.map((card) => (
            <div key={card.label} className={`appsStatChip balanceSheetStatChip ${card.tone}`}>
              <span>{card.label}</span>
              <strong>{formatKes(card.value)}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className="appsFiltersCard financeReportsToolbar balanceSheetToolbar">
        <div className="appsFilterGroup financeReportsToolbarGroup">
          <button type="button" className="opsGhostBtn financeReportsRefreshBtn" onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRotateRight} /> Refresh snapshot
          </button>
          <button
            type="button"
            className="opsGhostBtn financeReportsRefreshBtn"
            onClick={exportBalanceSheetPdf}
            disabled={loading || Boolean(error)}
          >
            <FontAwesomeIcon icon={faScaleBalanced} /> Download PDF
          </button>
          <div className="balanceSheetToolbarMeta">
            <span>Snapshot basis</span>
            <strong>Recorded transactions and balances</strong>
          </div>
        </div>
        <div className="financeReportsToolbarMeta">
          <span>{snapshotAt ? formatDateTime(snapshotAt) : 'Loading snapshot'}</span>
          <strong>{equity >= 0 ? 'Positive equity position' : 'Negative equity position'}</strong>
        </div>
      </div>

      {loading ? (
        <div className="opsLoadingCard financeReportsLoading">
          <Spinner animation="border" role="status" />
          <span>Loading balance sheet...</span>
        </div>
      ) : error ? (
        <div className="balanceSheetErrorCard">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <div>
            <strong>Could not load balance sheet</strong>
            <p>{error}</p>
          </div>
        </div>
      ) : (
        <>
          <section ref={statementRef} className="balanceSheetOfficialStatement financeReportsCard">
            <div className="balanceSheetOfficialHeader">
              <div className="balanceSheetOfficialBrand">
                <img src={logo} alt="Ubunifu College" className="balanceSheetOfficialLogo" />
                <div>
                  <p>Ubunifu College</p>
                  <h4>Statement of Financial Position</h4>
                  <span>As at {snapshotAt ? formatDateTime(snapshotAt) : 'Current snapshot'}</span>
                </div>
              </div>
              <div className="balanceSheetOfficialMeta">
                <div>
                  <span>Currency</span>
                  <strong>KES</strong>
                </div>
                <div>
                  <span>Basis</span>
                  <strong>Live management accounts</strong>
                </div>
              </div>
            </div>

            <div className="balanceSheetOfficialNotice">
              Prepared from the live finance system. Amounts are presented in Kenya Shillings and equity is the
              balancing figure from the recorded assets and liabilities.
            </div>

            <div className={`balanceSheetOfficialBalanceFlag ${balanceStatusTone}`}>
              <span>{balanceStatusTone === 'success' ? 'Balanced statement' : 'Review required'}</span>
              <strong>
                Assets {formatAccountingKes(totalAssets)} = Liabilities and Equity {formatAccountingKes(totalLiabilitiesAndEquity)}
              </strong>
            </div>

            <div className="balanceSheetOfficialGrid">
              <article className="balanceSheetOfficialPanel">
                <div className="balanceSheetOfficialPanelTitle">
                  <h5>Assets</h5>
                  <span>{formatAccountingKes(totalAssets)}</span>
                </div>

                {statementAssetRows.map((section) => (
                  <div key={section.section} className="balanceSheetOfficialSection">
                    <div className="balanceSheetOfficialSectionTitle">
                      <strong>{section.section}</strong>
                    </div>
                    <table className="balanceSheetOfficialTable">
                      <tbody>
                        {section.rows.map((row) => (
                          <tr key={row.label}>
                            <td>
                              <div className="balanceSheetOfficialRowMeta">
                                <strong>{row.label}</strong>
                                <span>{row.note}</span>
                              </div>
                            </td>
                            <td className="balanceSheetOfficialAmount">
                              {formatAccountingKes(row.amount)}
                            </td>
                          </tr>
                        ))}
                        <tr className="balanceSheetOfficialTotalRow">
                          <td>
                            <strong>{section.totalLabel}</strong>
                          </td>
                          <td className="balanceSheetOfficialAmount">
                            <strong>{formatAccountingKes(section.totalAmount)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </article>

              <article className="balanceSheetOfficialPanel">
                <div className="balanceSheetOfficialPanelTitle">
                  <h5>Liabilities and Equity</h5>
                  <span>{formatAccountingKes(totalLiabilitiesAndEquity)}</span>
                </div>

                {statementLiabilityRows.map((section) => (
                  <div key={section.section} className="balanceSheetOfficialSection">
                    <div className="balanceSheetOfficialSectionTitle">
                      <strong>{section.section}</strong>
                    </div>
                    <table className="balanceSheetOfficialTable">
                      <tbody>
                        {section.rows.map((row) => (
                          <tr key={row.label}>
                            <td>
                              <div className="balanceSheetOfficialRowMeta">
                                <strong>{row.label}</strong>
                                <span>{row.note}</span>
                              </div>
                            </td>
                            <td className="balanceSheetOfficialAmount">
                              {formatAccountingKes(row.amount)}
                            </td>
                          </tr>
                        ))}
                        <tr className="balanceSheetOfficialTotalRow">
                          <td>
                            <strong>{section.totalLabel}</strong>
                          </td>
                          <td className="balanceSheetOfficialAmount">
                            <strong>{formatAccountingKes(section.totalAmount)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                <div className="balanceSheetOfficialEquation">
                  <span>Total liabilities and equity</span>
                  <strong>{formatAccountingKes(totalLiabilitiesAndEquity)}</strong>
                </div>
              </article>
            </div>

            <div className="balanceSheetOfficialFooter">
              <div>
                <strong>Management note</strong>
                <span>
                  Asset values are taken from the asset register, while liabilities are built from open finance,
                  payroll, statutory, and loan records.
                </span>
              </div>
              <div>
                <strong>Prepared for</strong>
                <span>Investors, lenders, directors, and other external stakeholders.</span>
              </div>
            </div>
          </section>

          <section className="financeReportsStatsGrid balanceSheetStatsGrid">
            {summaryCards.map((card) => (
              <article key={card.label} className={`financeReportsStatCard balanceSheetStatCard ${card.tone}`}>
                <span>{card.label}</span>
                <strong>{formatKes(card.value)}</strong>
                <small>{card.note}</small>
              </article>
            ))}
          </section>

          <div className="financeReportsTopGrid balanceSheetTopGrid">
            <section className="financeReportsCard balanceSheetStatementCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div>
                  <h4>Assets</h4>
                  <p>Current assets and the book value of assets registered elsewhere in the app.</p>
                </div>
              </div>

              <div className="balanceSheetEquationCard">
                <div className={`balanceSheetEquationFlag ${balanceStatusTone}`}>
                  <FontAwesomeIcon icon={balanceStatusTone === 'success' ? faArrowTrendUp : faCircleExclamation} />
                  <span>{equity >= 0 ? 'Balanced snapshot' : 'Assets are below liabilities'}</span>
                </div>
                <strong>
                  {formatKes(totalAssets)} = {formatKes(totalLiabilities)} + {formatKes(equity)}
                </strong>
                <p>
                  Equity is derived as a balancing figure from the live operational snapshot, so it
                  reflects the records currently held in the app.
                </p>
              </div>

              <div className="balanceSheetStatementGrid">
                <div className="balanceSheetStatementPanel">
                  <div className="balanceSheetPanelHeader">
                    <h5>Current assets</h5>
                    <span>{formatKes(trackedCashAndBank + accountsReceivableTotal)}</span>
                  </div>
                  <div className="balanceSheetRowList">
                    {currentAssetRows.map((row) => (
                      <div key={row.label} className="balanceSheetRow">
                        <div>
                          <strong>{row.label}</strong>
                          <span>{row.note}</span>
                        </div>
                        <div className={`balanceSheetAmount ${row.amount >= 0 ? 'positive' : 'negative'}`}>
                          {formatKes(row.amount)}
                        </div>
                      </div>
                    ))}
                    <div className="balanceSheetRow total">
                      <div>
                        <strong>Total current assets</strong>
                        <span>Cash and receivables combined</span>
                      </div>
                      <div className="balanceSheetAmount">
                        {formatKes(trackedCashAndBank + accountsReceivableTotal)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="balanceSheetStatementPanel">
                  <div className="balanceSheetPanelHeader">
                    <h5>Non-current assets</h5>
                    <span>{formatKes(nonCurrentAssetsTotal)}</span>
                  </div>
                  <div className="balanceSheetRowList">
                    {assetCategoryRows.slice(0, 5).map((row) => (
                      <div key={row.category} className="balanceSheetRow">
                        <div>
                          <strong>{row.category}</strong>
                          <span>{row.count} asset{row.count === 1 ? '' : 's'} • {formatShortDate(row.latestDate)}</span>
                        </div>
                        <div className="balanceSheetAmount">
                          {formatKes(row.amount)}
                        </div>
                      </div>
                    ))}
                    <div className="balanceSheetRow total">
                      <div>
                        <strong>Total non-current assets</strong>
                        <span>Asset register book value</span>
                      </div>
                      <div className="balanceSheetAmount">
                        {formatKes(nonCurrentAssetsTotal)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <aside className="financeReportsCard balanceSheetChartCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faChartPie} />
                </div>
                <div>
                  <h4>Asset mix</h4>
                  <p>How the asset base is composed across the records captured in the app.</p>
                </div>
              </div>

              <div className="financeReportsDonutWrap">
                {assetMixData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={assetMixData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={68}
                        outerRadius={110}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {assetMixData.map((entry, index) => (
                          <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={pieTooltip} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="balanceSheetEmptyState">
                    <FontAwesomeIcon icon={faWallet} />
                    <strong>No asset values available yet</strong>
                    <p>Add assets to the register so the chart can show composition.</p>
                  </div>
                )}
              </div>

              <div className="financeReportsExposureFooter financeReportsExposureFooter--chart">
                <p>
                  Cash & bank: <strong>{formatKes(trackedCashAndBank)}</strong>.
                  {' '}
                  Receivables: <strong>{formatKes(accountsReceivableTotal)}</strong>.
                </p>
              </div>

              <div className="balanceSheetKeyFacts">
                <div className="balanceSheetKeyFact">
                  <span>Total assets</span>
                  <strong>{formatKes(totalAssets)}</strong>
                </div>
                <div className="balanceSheetKeyFact">
                  <span>Total liabilities</span>
                  <strong>{formatKes(totalLiabilities)}</strong>
                </div>
                <div className="balanceSheetKeyFact">
                  <span>Equity</span>
                  <strong>{formatKes(equity)}</strong>
                </div>
              </div>
            </aside>
          </div>

          <div className="balanceSheetBottomGrid">
            <section className="financeReportsCard balanceSheetLedgerCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faLandmark} />
                </div>
                <div>
                  <h4>Accounts receivable</h4>
                  <p>Students still owing fees, ranked from the largest balances down.</p>
                </div>
              </div>

              {topReceivables.length === 0 ? (
                <div className="balanceSheetEmptyState balanceSheetEmptyState--compact">
                  <strong>No outstanding student balances</strong>
                  <p>Once a student still owes a fee balance, they will appear here automatically.</p>
                </div>
              ) : (
                <div className="balanceSheetLedgerList">
                  {topReceivables.map((row) => (
                    <div key={row.id} className="balanceSheetLedgerRow">
                      <div>
                        <strong>{row.studentName}</strong>
                        <span>{row.course} • {row.campus}</span>
                        <small>Paid {formatKes(row.paidAmount)} of {formatKes(row.totalCourseFee)}</small>
                      </div>
                      <div className="balanceSheetLedgerValue">
                        <strong>{formatKes(row.balance)}</strong>
                        <span>{formatShortDate(row.latestPaymentDate)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="financeReportsCard balanceSheetLedgerCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faScaleBalanced} />
                </div>
                <div>
                  <h4>Liabilities</h4>
                  <p>Open obligations collected from finance, payroll, and loan modules.</p>
                </div>
              </div>

              <div className="balanceSheetLiabilityPanels">
                <div className="balanceSheetLiabilityPanel">
                  <div className="balanceSheetPanelHeader">
                    <h5>Current liabilities</h5>
                    <span>{formatKes(currentLiabilitiesTotal)}</span>
                  </div>
                  <div className="balanceSheetRowList">
                    {liabilityBreakdownRows.slice(0, 5).map((row) => (
                      <div key={row.label} className="balanceSheetRow">
                        <div>
                          <strong>{row.label}</strong>
                          <span>{row.note}</span>
                        </div>
                        <div className="balanceSheetAmount negative">
                          {formatKes(row.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="balanceSheetLiabilityPanel">
                  <div className="balanceSheetPanelHeader">
                    <h5>Long-term liabilities</h5>
                    <span>{formatKes(longTermLoanOutstandingTotal)}</span>
                  </div>
                  {longTermLoanRows.length === 0 ? (
                    <div className="balanceSheetEmptyState balanceSheetEmptyState--compact">
                      <strong>No long-term liabilities found</strong>
                      <p>Loans without a due date or beyond 12 months will appear here.</p>
                    </div>
                  ) : (
                    <div className="balanceSheetLedgerList">
                      {longTermLoanRows.slice(0, 5).map((row) => (
                        <div key={row.id} className="balanceSheetLedgerRow">
                          <div>
                            <strong>{row.title}</strong>
                            <span>{row.counterparty}</span>
                            <small>Due {formatShortDate(row.dueDate)}</small>
                          </div>
                          <div className="balanceSheetLedgerValue">
                            <strong>{formatKes(row.remainingAmount)}</strong>
                            <span>{formatShortDate(row.lastPaymentDate)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>

          <section className="financeReportsCard balanceSheetSourcesCard">
            <div className="opsSectionHeader financeReportsSectionHeader">
              <div className="opsSectionIcon">
                <FontAwesomeIcon icon={faCircleExclamation} />
              </div>
              <div>
                <h4>Data stitched from across the app</h4>
                <p>These totals are assembled from multiple modules so the page reflects the live system.</p>
              </div>
            </div>

            <div className="balanceSheetSourceGrid">
              {sourceRows.map((row) => (
                <div key={row.label} className="balanceSheetSourceCard">
                  <span>{row.label}</span>
                  <strong>{formatKes(row.amount)}</strong>
                  <small>{row.count} record{row.count === 1 ? '' : 's'} • {row.note}</small>
                </div>
              ))}
            </div>

            <div className="balanceSheetSourcePills">
              <div className="financeReportsInsightPill">
                <FontAwesomeIcon icon={faWallet} />
                <span>
                  Cash position is tracked as <strong>recorded inflows minus recorded outflows</strong>.
                </span>
              </div>
              <div className={`financeReportsInsightPill ${equity >= 0 ? 'positive' : 'negative'}`}>
                <FontAwesomeIcon icon={equity >= 0 ? faArrowTrendUp : faCircleExclamation} />
                <span>
                  Equity is the balancing figure: <strong>{formatKes(equity)}</strong>.
                </span>
              </div>
            </div>
          </section>

          <section className="financeReportsCard balanceSheetLedgerCard">
            <div className="opsSectionHeader financeReportsSectionHeader">
              <div className="opsSectionIcon">
                <FontAwesomeIcon icon={faScaleBalanced} />
              </div>
              <div>
                <h4>Current obligations</h4>
                <p>Top unpaid items across bills, requisitions, payroll, statutory remittances, and loans.</p>
              </div>
            </div>

            {topLiabilities.length === 0 ? (
              <div className="balanceSheetEmptyState balanceSheetEmptyState--compact">
                <strong>No current liabilities found</strong>
                <p>Once finance records outstanding balances, they will show here automatically.</p>
              </div>
            ) : (
              <div className="balanceSheetLedgerList balanceSheetLedgerList--dense">
                {topLiabilities.map((row) => (
                  <div key={row.id} className="balanceSheetLedgerRow">
                    <div>
                      <strong>{row.title}</strong>
                      <span>{row.meta}</span>
                      <small>Updated {formatShortDate(row.date)}</small>
                    </div>
                    <div className="balanceSheetLedgerValue">
                      <strong>{formatKes(row.amount)}</strong>
                      <span>{row.tone === 'info' ? 'Payroll' : row.tone === 'warning' ? 'Due soon' : 'Open'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default BalanceSheetComp;
